import { hasInboxSyncConsent, INBOX_DISCLOSURE, INBOX_DISCLOSURE_VERSION, PRIVACY_URL, TERMS_URL, type InboxSyncConsent } from "@gmx/shared/features/privacy";
import { Button, Callout, Card, Checkbox, Spinner } from "@gmx/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export function InboxPrivacyCard() {
  const { isImpersonating } = useAuth();
  const qc = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const choice = useQuery({ queryKey: ["privacy", "inbox-sync"], queryFn: () => api<InboxSyncConsent>("/api/privacy"), refetchInterval: 60_000 });
  const save = useMutation({
    mutationFn: (inboxSync: boolean) => api<InboxSyncConsent>("/api/privacy/inbox-sync", { method: "PUT", body: { inboxSync, disclosureVersion: INBOX_DISCLOSURE_VERSION } }),
    onSuccess: result => { qc.setQueryData(["privacy", "inbox-sync"], result); void qc.invalidateQueries({ queryKey: ["settings"] }); setAgreed(false); },
  });
  const enabled = hasInboxSyncConsent(choice.data);
  return <Card title="Your inbox sync choice" subtitle="Choose how your X conversations connect to Grow My X.">
    <div className="gf-stack">
      <p>{INBOX_DISCLOSURE.collection}</p>
      <p>{INBOX_DISCLOSURE.ai}</p>
      <p>{INBOX_DISCLOSURE.pause}</p>
      <p><a className="gf-link" href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>{" · "}<a className="gf-link" href={TERMS_URL} target="_blank" rel="noreferrer">Terms of Service</a></p>
      {choice.isPending ? <Spinner label="Loading your privacy choice" /> : choice.isError ? <Callout tone="danger" title="Couldn't load your privacy choice" action={<Button size="sm" onClick={() => void choice.refetch()}>Try again</Button>}>Inbox sync waits until your choice can be checked.</Callout> : enabled ? <>
        <Callout tone="success" title="Inbox sync enabled">This choice applies to your connected X accounts. You can turn it off at any time.</Callout>
        <Button variant="secondary" loading={save.isPending} disabled={isImpersonating} onClick={() => save.mutate(false)}>Turn off inbox sync</Button>
      </> : <>
        <Callout tone="info" title={choice.data.revokedAt ? "Inbox sync is off" : "Inbox sync is waiting for your choice"}>{INBOX_DISCLOSURE.migration}</Callout>
        <Checkbox checked={agreed} onChange={setAgreed} disabled={isImpersonating || save.isPending} label={INBOX_DISCLOSURE.choice} />
        <Button variant="primary" disabled={!agreed || isImpersonating} loading={save.isPending} onClick={() => save.mutate(true)}>Enable inbox sync</Button>
      </>}
      {isImpersonating ? <Callout>The account owner makes this choice from their own session.</Callout> : null}
      {save.isError ? <Callout tone="danger" title="Your choice wasn't saved">{save.error instanceof Error ? save.error.message : "Try again."}</Callout> : null}
    </div>
  </Card>;
}
